/**
 * お問合せ: 入力 → 確認 → 完了（静的ビルド用 sessionStorage 連携）
 */
import { initContactInquiryFormValidation, validateContactInquiryForm } from './contact-inquiry-form-validation.js';

const STORAGE_KEY = 'takamatsu_contact_inquiry_v1';
const CONFIRM_PATH = '/contact_inquiry_confirm.html';
const COMPLETE_PATH = '/contact_inquiry_complete.html';

/** デザイン確認用。本番では false にし、データ無し時は empty を表示 */
const CONTACT_INQUIRY_CONFIRM_PREVIEW_ALWAYS_VISIBLE = true;

function getFormRoot() {
	return document.querySelector('form[data-contact-inquiry-flow]#contact-inquiry-form');
}

function collectRawFormFields(form) {
	const fd = new FormData(form);
	const pick = (name) => (fd.get(name) ?? '').toString();
	return {
		ciq_name_family: pick('ciq_name_family').trim(),
		ciq_name_given: pick('ciq_name_given').trim(),
		ciq_tel: pick('ciq_tel').trim(),
		ciq_email: pick('ciq_email').trim(),
		ciq_zip: pick('ciq_zip').trim(),
		ciq_pref: pick('ciq_pref').trim(),
		ciq_city: pick('ciq_city').trim(),
		ciq_street: pick('ciq_street').trim(),
		ciq_building: pick('ciq_building').trim(),
		ciq_message: pick('ciq_message'),
	};
}

function collectFormPayload(form) {
	const fd = new FormData(form);
	const prefEl = form.querySelector('#ciq-pref');
	const prefLabel =
		prefEl && prefEl.selectedIndex >= 0 ? prefEl.options[prefEl.selectedIndex].text.trim() : '';
	const family = (fd.get('ciq_name_family') || '').toString().trim();
	const given = (fd.get('ciq_name_given') || '').toString().trim();
	const nameFull = [family, given].filter(Boolean).join(' ') || '—';

	const zipRaw = (fd.get('ciq_zip') || '').toString().trim();
	const postalMark = '〒';
	const zipDisplay = zipRaw
		? `${postalMark} ${zipRaw.replace(new RegExp(`^${postalMark}\\s*`), '')}`
		: '';

	const pref = (fd.get('ciq_pref') || '').toString().trim();
	const city = (fd.get('ciq_city') || '').toString().trim();
	const street = (fd.get('ciq_street') || '').toString().trim();
	const building = (fd.get('ciq_building') || '').toString().trim();
	const prefDisplay = prefLabel && prefLabel !== '都道府県' ? prefLabel : pref || '';

	const line2 = [prefDisplay, city, street].filter(Boolean).join(' ');
	const lines = [zipDisplay, line2, building].filter((line) => line && line.length > 0);
	const addressBlock = lines.length ? lines.join('\n') : '—';

	const message = (fd.get('ciq_message') || '').toString().trim();

	return {
		ciq_name_full: nameFull,
		ciq_tel: (fd.get('ciq_tel') || '').toString().trim() || '—',
		ciq_email: (fd.get('ciq_email') || '').toString().trim() || '—',
		ciq_address_block: addressBlock,
		ciq_message: message || '—',
	};
}

function saveDraft(payload) {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
	} catch {
		/* ignore */
	}
}

function loadDraft() {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function clearDraft() {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		/* ignore */
	}
}

function applyRestoreToForm(form, raw) {
	if (!raw || typeof raw !== 'object') return;

	for (const key of Object.keys(raw)) {
		const val = raw[key];
		const str = val == null ? '' : String(val);
		const el = form.elements.namedItem(key);
		if (!el || el instanceof RadioNodeList) continue;
		if (el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
			el.value = str;
		}
	}
}

function initFormPage() {
	const form = getFormRoot();
	if (!form) return;

	initContactInquiryFormValidation(form);

	const draft = loadDraft();
	if (draft && draft._restore && typeof draft._restore === 'object') {
		applyRestoreToForm(form, draft._restore);
	}

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		if (!validateContactInquiryForm(form)) {
			return;
		}
		const confirmPayload = collectFormPayload(form);
		const raw = collectRawFormFields(form);
		saveDraft({ ...confirmPayload, _restore: raw });
		window.location.href = CONFIRM_PATH;
	});
}

function setPreFormattedDesc(el, text) {
	if (!el) return;
	el.replaceChildren();
	const lines = String(text).split('\n');
	lines.forEach((line) => {
		const span = document.createElement('span');
		span.className = 'visit__confirmPreLine';
		span.textContent = line;
		el.appendChild(span);
	});
}

function setTextContent(el, text) {
	if (!el) return;
	el.textContent = text;
}

function initConfirmPage() {
	const page = document.querySelector('[data-contact-inquiry-page="confirm"]');
	if (!page) return;

	const block = document.getElementById('contact-inquiry-confirm-block');
	const empty = document.getElementById('contact-inquiry-confirm-empty');
	const shell = document.querySelector('.contactInquiry__confirmShell');
	const draft = loadDraft();

	if (!block || !empty) return;

	const sendBtn = document.getElementById('contact-inquiry-confirm-send');
	if (sendBtn) {
		sendBtn.addEventListener('click', () => {
			clearDraft();
			window.location.assign(COMPLETE_PATH);
		});
	}

	if (!draft) {
		if (CONTACT_INQUIRY_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			if (shell) shell.hidden = false;
			block.hidden = false;
			empty.hidden = true;
		} else {
			if (shell) shell.hidden = true;
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	const dash = '—';
	const addressRequired = !draft._meta?.addressOptional;
	const hasRequired =
		draft.ciq_name_full !== dash &&
		draft.ciq_tel !== dash &&
		draft.ciq_email !== dash &&
		(!addressRequired || draft.ciq_address_block !== dash);

	if (!hasRequired) {
		if (CONTACT_INQUIRY_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			if (shell) shell.hidden = false;
			block.hidden = false;
			empty.hidden = true;
		} else {
			if (shell) shell.hidden = true;
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	if (shell) shell.hidden = false;
	block.hidden = false;
	empty.hidden = true;

	page.querySelectorAll('[data-ciq-field]').forEach((el) => {
		const key = el.getAttribute('data-ciq-field');
		if (!key || !(key in draft)) return;
		const val = draft[key];
		const out = typeof val === 'boolean' ? (val ? '同意済み' : dash) : String(val);
		if (el.classList.contains('visit__confirmDesc--pre')) {
			setPreFormattedDesc(el, out);
		} else {
			setTextContent(el, out);
		}
	});
}

export function initContactInquiryFlow() {
	const pageType = document
		.querySelector('[data-contact-inquiry-page]')
		?.getAttribute('data-contact-inquiry-page');
	if (!pageType) return;
	if (pageType === 'form') initFormPage();
	if (pageType === 'confirm') initConfirmPage();
}
